/**
 * Converts RGB to hex color.
 * @param r The red value.
 * @param g The green value.
 * @param b The blue value.
 * @returns The hex color.
 */
export function RGBToHex(r, g, b) {
    let red = r.toString(16);
    let green = g.toString(16);
    let blue = b.toString(16);
    if (red.length == 1)
        red = "0" + red;
    if (green.length == 1)
        green = "0" + green;
    if (blue.length == 1)
        blue = "0" + blue;
    return "#" + red + green + blue;
}
/**
 * Converts hex color to RGB.
 * @param hex The hex color to convert.
 * @returns The RGB color.
 */
export function HexToRGB(hex) {
    let r = 0, g = 0, b = 0;
    if (hex.length == 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    }
    else if (hex.length == 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return [r, g, b];
}
/**
 * Converts hex color to HSL.
 * @param hex The hex color to convert.
 * @returns The HSL color.
 */
export function HexToHSL(hex) {
    const [r, g, b] = HexToRGB(hex);
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let l = (max + min) / 2;
    let h = 0, s = 0;
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
        case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
        case g:
            h = ((b - r) / d + 2) / 6;
            break;
        case b:
            h = ((r - g) / d + 4) / 6;
            break;
    }
    return [h, s, l];
}
/**
 * Converts HSL to hex color.
 * @param h The hue value.
 * @param s The saturation value.
 * @param l The lightness value.
 * @returns The hex color.
 */
export function HSLToHex(h, s, l) {
    let r = 0, g = 0, b = 0;
    if (s == 0) {
        r = g = b = l;
    }
    else {
        function hue2rgb(p, q, t) {
            if (t < 0)
                t += 1;
            if (t > 1)
                t -= 1;
            if (t < 1 / 6)
                return p + (q - p) * 6 * t;
            if (t < 1 / 2)
                return q;
            if (t < 2 / 3)
                return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }
        let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        let p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return RGBToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255));
}
/**
 * Converts hex color to HSV.
 * @param hex The hex color to convert.
 * @returns The HSV color.
 */
export function HexToHSV(hex) {
    const [r, g, b] = HexToRGB(hex);
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let v = max;
    let s = max == 0 ? 0 : 1 - min / max;
    let h = 0;
    if (max != min) {
        switch (max) {
            case r:
                h = (g - b) / (max - min) + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / (max - min) + 2;
                break;
            case b:
                h = (r - g) / (max - min) + 4;
                break;
        }
        h /= 6;
    }
    return [h, s, v];
}
/**
 * Converts HSV to hex color.
 * @param h The hue value.
 * @param s The saturation value.
 * @param v The value value.
 * @returns The hex color.
 */
export function HSVToHex(h, s, v) {
    let r = 0, g = 0, b = 0;
    if (s == 0) {
        r = g = b = v;
    }
    else {
        h *= 6;
        let i = Math.floor(h);
        let f = h - i;
        let p = v * (1 - s);
        let q = v * (1 - s * f);
        let t = v * (1 - s * (1 - f));
        switch (i) {
            case 0:
                r = v;
                g = t;
                b = p;
                break;
            case 1:
                r = q;
                g = v;
                b = p;
                break;
            case 2:
                r = p;
                g = v;
                b = t;
                break;
            case 3:
                r = p;
                g = q;
                b = v;
                break;
            case 4:
                r = t;
                g = p;
                b = v;
                break;
            case 5:
                r = v;
                g = p;
                b = q;
                break;
        }
    }
    return RGBToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255));
}
/**
 * Converts a string to a hex color. Always produces the same color for the same input.
 * @param input any string
 * @returns hex color
 */
export function stringToHexColor(input) {
    let hash = 0;
    // Generate hash from string
    for (let i = 0; i < input.length; i++) {
        hash = input.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Convert the hash to a hex color
    let color = "#";
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
}
/**
 * Gets the contrast color for a given hex color.
 * @param hex The hex color to get the contrast color for.
 * @returns black or white depending on the contrast of the given color.
 */
export function getContrastColorBW(hex) {
    const [r, g, b] = HexToRGB(hex);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000000" : "#ffffff";
}
//# sourceMappingURL=ColorConvert.js.map